<template>
  <div class="digital-human" :class="'state-' + state">
    <!-- Character container -->
    <div class="avatar-wrapper">
      <img
        v-if="avatarSrc"
        :src="avatarSrc"
        class="avatar-img"
        alt="robot"
      />
      <div v-else class="avatar-placeholder">
        <span class="avatar-emoji">🤖</span>
      </div>

      <!-- Listening: wave rings -->
      <div v-if="state === 'listening'" class="wave-rings">
        <span class="ring ring-1"></span>
        <span class="ring ring-2"></span>
        <span class="ring ring-3"></span>
      </div>

      <!-- Thinking: dots bubble -->
      <div v-if="state === 'thinking'" class="think-bubble">
        <span class="dot">●</span>
        <span class="dot">●</span>
        <span class="dot">●</span>
      </div>

      <!-- Speaking: mouth wave bars -->
      <div v-if="state === 'speaking'" class="mouth-bars">
        <span class="bar bar-1"></span>
        <span class="bar bar-2"></span>
        <span class="bar bar-3"></span>
        <span class="bar bar-4"></span>
        <span class="bar bar-5"></span>
      </div>
    </div>

    <!-- Subtitle area -->
    <div class="subtitle" v-if="text">
      {{ text }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'DigitalHuman',
  props: {
    state: {
      type: String,
      default: 'idle',
      validator: (v) => ['idle', 'listening', 'thinking', 'speaking'].includes(v),
    },
    text: {
      type: String,
      default: '',
    },
    avatarSrc: {
      type: String,
      default: '',
    },
  },
}
</script>

<style scoped>
.digital-human {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  user-select: none;
}

.avatar-wrapper {
  position: relative;
  width: 200px;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* === IDLE: breathing === */
.state-idle .avatar-img,
.state-idle .avatar-placeholder {
  animation: breathe 3s ease-in-out infinite;
}

@keyframes breathe {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.03); }
}

.avatar-img {
  width: 180px;
  height: 180px;
  object-fit: contain;
  border-radius: 50%;
}

.avatar-placeholder {
  width: 180px;
  height: 180px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}

.avatar-emoji {
  font-size: 80px;
}

/* === LISTENING: wave rings === */
.wave-rings {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
}

.ring {
  position: absolute;
  border: 2px solid #22c55e;
  border-radius: 50%;
  animation: waveOut 1.2s ease-out infinite;
}
.ring-1 { width: 60px; height: 60px; animation-delay: 0s; margin: -30px 0 0 -30px; }
.ring-2 { width: 60px; height: 60px; animation-delay: 0.4s; margin: -30px 0 0 -30px; }
.ring-3 { width: 60px; height: 60px; animation-delay: 0.8s; margin: -30px 0 0 -30px; }

@keyframes waveOut {
  0%   { transform: scale(0.5); opacity: 1; }
  100% { transform: scale(3.5); opacity: 0; }
}

/* === THINKING: dot animation === */
.think-bubble {
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  background: #f1f5f9;
  border-radius: 12px;
  padding: 6px 14px;
  display: flex;
  gap: 4px;
}

.dot {
  font-size: 10px;
  animation: dotBlink 0.6s step-end infinite;
}
.dot:nth-child(2) { animation-delay: 0.2s; }
.dot:nth-child(3) { animation-delay: 0.4s; }

@keyframes dotBlink {
  0%, 100% { opacity: 0.2; }
  50% { opacity: 1; }
}

/* === SPEAKING: mouth bars === */
.mouth-bars {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 3px;
  align-items: flex-end;
  height: 30px;
}

.bar {
  width: 6px;
  background: #ef4444;
  border-radius: 3px;
  animation: barJump 0.5s ease-in-out infinite alternate;
}
.bar-1 { height: 10px; animation-delay: 0s; }
.bar-2 { height: 20px; animation-delay: 0.1s; }
.bar-3 { height: 15px; animation-delay: 0.2s; }
.bar-4 { height: 25px; animation-delay: 0.3s; }
.bar-5 { height: 12px; animation-delay: 0.4s; }

@keyframes barJump {
  0% { transform: scaleY(0.4); }
  100% { transform: scaleY(1); }
}

/* === Subtitle === */
.subtitle {
  margin-top: 16px;
  padding: 8px 20px;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border-radius: 20px;
  font-size: 14px;
  max-width: 300px;
  text-align: center;
  min-height: 20px;
  transition: opacity 0.2s;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .avatar-wrapper {
    width: 150px;
    height: 150px;
  }
  .avatar-img,
  .avatar-placeholder {
    width: 130px;
    height: 130px;
  }
  .avatar-emoji {
    font-size: 60px;
  }
  .subtitle {
    max-width: 260px;
    font-size: 13px;
  }
}
</style>
